import { hardNavigation } from "../../../utils/hard-navigation.util";
import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";

import { AuthService } from "./auth.service";
import { NavigationService } from "./navigation.service";
import { LoadingService } from "./loader.service";
import { SharedDataService } from "./shared-data.service";
import { CrossAppAuthService } from "./cross-app-auth.service";
import { MasqueradeService } from "./masquerade.service";
import { NotificationService } from "./notification.service";
import { CAMPAIGN_REDIRECT_STORAGE_KEY } from "../constants/campaign.constants";
import { ONBOARDING_REDIRECT_STORAGE_KEY } from "../constants/onboarding-redirect.constants";
import { COUPON_REDIRECT_STORAGE_KEY } from "../constants/coupon-redirect.constants";
import moment from "moment";

@Injectable()
export class AuthGuardService  {
  constructor(
    private readonly authService: AuthService,
    private readonly sharedData: SharedDataService,
    private readonly nav: NavigationService,
    private readonly loadingService: LoadingService,
    private readonly crossAppAuth: CrossAppAuthService,
    private readonly masqueradeService: MasqueradeService,
    private readonly notification: NotificationService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    // Check masquerade expiration FIRST
    if (this.masqueradeService.isMasquerading()) {
      if (this.masqueradeService.isMasqueradeExpired()) {
        this.notification.toast("Masquerade session expired", "warning");
        this.masqueradeService.endMasquerade().subscribe({
          next: (cmcUrl) => {
            hardNavigation.navigate(cmcUrl);
          },
          error: (err) => {
            console.error("Failed to end masquerade:", err);
            // Clear session manually if service fails
            sessionStorage.clear();
            this.nav.navigateTo(["/session/signin"]);
          },
        });
        return false;
      }

      // Warn if < 10 minutes remaining (show once per session)
      const remaining = this.masqueradeService.getRemainingTime();
      if (remaining <= 10 && !sessionStorage.getItem("masquerade-warning-shown")) {
        sessionStorage.setItem("masquerade-warning-shown", "true");
        this.notification.toast(
          `Masquerade session expires in ${remaining} minutes`,
          "warning",
          5000
        );
      }
    }

    const url: string = state.url;

    return this.checkLogin(url);
  }

  checkLogin(url?: string): boolean {
    if (this.isLoggedIn()) {
      const userDetails = this.authService.userDetails.user_details;

      if (userDetails?.email_verified_at) {
        this.sharedData.deleteAttribute("email_to_verify");
        return true;
      }

      this.sharedData.setAttribute("email_to_verify", userDetails.email);
      const hasTimer = this.sharedData.getAttribute("timer_to_resend");
      if (!hasTimer) {
        this.sharedData.setAttribute("timer_to_resend", moment().add(60, "seconds"));
      }

      this.nav.navigateTo(["/session/verify-email"]);
      return true;
    }

    // Store the attempted URL for redirecting
    if (url) {
      this.authService.redirectUrl = url;

      // AuthGuardService's redirect below is a hard page navigation, which
      // wipes the in-memory redirectUrl above. Campaign deep-links need to
      // survive that reload, so stash them in sessionStorage too.
      if (url.includes("campaign=")) {
        sessionStorage.setItem(CAMPAIGN_REDIRECT_STORAGE_KEY, url);
      }

      // Same problem for a signed-out click on a marketing-email onboarding
      // URL (/onboarding, /onboarding0-9, /retakeonboarding — all resolved
      // to this query param by app.routing.ts before this guard runs): the
      // hard reload below would otherwise drop the "open the modal" intent
      // and strand the user on a bare /flyboard after login.
      if (url.includes("showonboardingprogresstraining=true")) {
        sessionStorage.setItem(ONBOARDING_REDIRECT_STORAGE_KEY, url);
      }

      // Same problem for a signed-out click on the FlyGrammar offer link
      // (/pricing?coupon=<code>, issue #592): without this stash the code
      // would be silently lost at the hard reload below, and the user would
      // land back on /flyboard at full price after signing in.
      if (url.includes("coupon=")) {
        sessionStorage.setItem(COUPON_REDIRECT_STORAGE_KEY, url);
      }
    }

    // For split apps (admin/CMC): redirect to main app for authentication
    if (!this.crossAppAuth.isMainApp) {
      this.crossAppAuth.redirectToMainAppForAuth(url);
      return false;
    }

    // Navigate to the login page with extras
    if (!window.document.URL.includes("session")) {
      window.open(window.document.baseURI + "session/signin", "_self");
    }
    return false;
  }

  validateStatus() {
    setTimeout(() => {
      if (this.checkLogin()) {
        if (window.document.URL.includes("signin")) {
          window.open(window.document.baseURI + "flyboard", "_self");
          return;
        }
      } else this.validateStatus();
    }, 3000);
  }

  isLoggedIn() {
    return Boolean(this.authService.accessToken && this.authService.userDetails);
  }
}
